import React, { Component } from 'react'
import PropTypes from 'prop-types'

import classNames from 'classnames'
import Loader from "./Loader"
import { Link } from 'react-router-dom'
import VehicleListItem from './VehicleListItem'
import VehicleFilter from './VehicleFilter'
import Media from '../components/Media'
import { getPathAlias, getModelUrl } from 'tw-oi-core/utils/vehicle'
import { getVehicleSubTypes, filterVehiclesBySubType, isLegacyYear } from '../extras/utils'
import { MESSAGE, ROUTE, PRODUCT_MAP, PRODUCT_SUBTYPE_MAP, UI } from '../config'

import '../styles/VehicleList.scss'
import Icon from './Icon'


export default class VehicleList extends Component {

  state = {
    currentFilter: null,
    filterOpened: false
  }

  static propTypes = {
    vehicles: PropTypes.array,
    groups: PropTypes.array,
    currentYear: PropTypes.string.isRequired,
    productType: PropTypes.string,
    heroShotMap: PropTypes.object,
    setVehicleModel: PropTypes.func.isRequired,
    getHeroShot: PropTypes.func.isRequired,
    isDesktop: PropTypes.bool,
    baseRoute: PropTypes.string.isRequired,
    getModelLink: PropTypes.func,
    showBrandSubtitle: PropTypes.bool,
    hideSubtypeFilter: PropTypes.bool,
    listVariant: PropTypes.oneOf(['default', 'unified']),
  }

  static defaultProps = {
    showBrandSubtitle: false,
    hideSubtypeFilter: false,
    listVariant: 'default',
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.vehicles !== this.props.vehicles) {
      this.onChangeFilter(null)
    }
    // scroll to top when list updated
    if (this.scrollArea && (prevState.currentFilter !== this.state.currentFilter || prevProps.vehicles !== this.props.vehicles)) {
      this.scrollArea.scrollTop = 0

      // scroll to top when list updated and header is sticky
      if (this.props.isDesktop && this.scrollArea.getBoundingClientRect().top < UI.STICKY_VEHICLES_BAR_HEIGHT) {
        window.scrollTo(window.pageXOffset, UI.STICKY_VEHICLES_BAR_HEIGHT)
      }
    }
  }

  onChangeFilter = filter => {
    this.setState({ currentFilter: filter })
  }

  onSwitchFilter = opened => {
    this.setState({ filterOpened: opened })
  }

  renderList = (items, renderGroups = false) => {
    const {
      baseRoute, currentYear, setVehicleModel, heroShotMap, productType, getHeroShot, isDesktop,
      getModelLink, showBrandSubtitle, listVariant,
    } = this.props
    const itemVariant = listVariant === 'unified' ? 'unified' : 'default'
    const unifiedCardType = renderGroups ? 'productType' : 'model'

    let list = []
    if (renderGroups) {
      items.forEach(item => {
        const product = PRODUCT_MAP[item] || {}
        if (product.title) {
          list.push(
            <VehicleListItem
              key={item}
              title={product.title}
              subtitle={listVariant === 'unified' ? UI.ALL_TYPES : (this.props.isDesktop ? UI.ALL_TYPES : null)}
              heroshot={{ vehicleProductType: item }}
              link={`${baseRoute}${ROUTE.VEHICLES}/${currentYear}/${getPathAlias(item)}`}
              isDesktop={isDesktop}
              variant={itemVariant}
              unifiedCardType={unifiedCardType}
            />
          )
        } else {
          // eslint-disable-next-line no-console
          console.error(`Couldn't find product by key ${item}`)
        }
      })
    } else {
      for (let i = 0; i < items.length; i++) {
        const vehicle = items[i]
        const { model } = vehicle
        const modelLink = typeof getModelLink === 'function'
          ? getModelLink(vehicle)
          : `${baseRoute}${ROUTE.GUIDE}${getModelUrl(currentYear, `${vehicle.id} ${model}`)}${ROUTE.BROWSE}`

        list.push(
          <VehicleListItem
            key={vehicle.id}
            heroshot={{ vehicle, vehicleProductType: vehicle.FOREST_RIVER_PRODUCT_TYPE || productType, heroShotMap, productType: vehicle.FOREST_RIVER_PRODUCT_TYPE || productType, getHeroShot, imageSize: isDesktop ? UI.HERO_SHOT_MEDIUM_SIZE : UI.HERO_SHOT_SMALL_SIZE }}
            title={isLegacyYear(currentYear) && vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE ?
              PRODUCT_SUBTYPE_MAP[vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE] :
              model}
            subtitle={showBrandSubtitle ?
              vehicle.make :
              (!isLegacyYear(currentYear) || !vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE ?
                PRODUCT_SUBTYPE_MAP[vehicle.FOREST_RIVER_PRODUCT_SUB_TYPE] :
                null)
            }
            link={modelLink}
            onClick={() => setVehicleModel && setVehicleModel(vehicle)}
            variant={itemVariant}
            unifiedCardType={unifiedCardType}
          />
        )
      }
    }

    return list
  }

  /**
   * Renders back link for mobile for vehicles with more than 1 item or plain text if there is only one item
   * @param {String} title
   * @param {Boolean} isSingleItem
   * @returns {HTMLElement|React.Component}
   */
  renderBackLinkMobile = (title, isSingleItem) => {
    const { baseRoute } = this.props

    if (isSingleItem) {
      return <span className="back">
        {title}
      </span>
    }

    return <Link className="back" to={`${baseRoute}${ROUTE.VEHICLES}/${this.props.currentYear}/`}>
      <Icon type="arrow-left" />
      {title}
    </Link>
  }

  render() {
    const { currentFilter, filterOpened } = this.state
    const { currentYear, productType, vehicles, groups, hideSubtypeFilter, listVariant } = this.props
    const showToolbar = productType || listVariant !== 'unified'

    // in case vehicle group has only one model, we should skip 'ALL_MODELS' breadcrumb
    const isSingleItem = !isLegacyYear(currentYear) && groups && groups.length === 1

    if (productType && !vehicles.length) {
      return <Loader type="status">{MESSAGE.EMPTY_VEHICLES}</Loader>
    }

    return <div className={classNames('VehicleList', listVariant === 'unified' && 'VehicleList--unified')}>

      {showToolbar && productType &&
        <Media type="mobile">
          <div className="vehicles-bar">
            {
              PRODUCT_MAP[productType].title &&
              this.renderBackLinkMobile(PRODUCT_MAP[productType].title, isSingleItem)
            }
            {!isLegacyYear(currentYear) && !hideSubtypeFilter && <VehicleFilter
              items={getVehicleSubTypes(vehicles)}
              onChange={this.onChangeFilter}
              onSwitch={this.onSwitchFilter}
            />}
          </div>
        </Media>
      }

      <div className={classNames("vehicles-content", filterOpened && "darkened")}>
        <div className="vehicles-container" ref={div => this.scrollArea = div}>
          <div className="vehicles-inner">
            {this.renderList(productType ? filterVehiclesBySubType(vehicles, currentFilter) : groups, !productType)}
          </div>
        </div>
      </div>
    </div>
  }
}
